import React, { useState, useEffect, useRef, useCallback } from "react";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/_core/hooks/useAuth";
import { Button } from "@/components/ui/button";
import {
  Users,
  Volume2,
  VolumeX,
  Maximize2,
  Scissors,
  CheckCircle2,
  Loader2,
  Wifi,
  Image as ImageIcon,
  Video,
  QrCode,
} from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { summarizeMobileQueueStatuses } from "@shared/mobileOperations";
import { MobileQueueStatusBar } from "@/components/MobileOperationControls";
import { ProductSalesPanel } from "@/components/ProductSalesPanel";
import { getQueuePanelProfile } from "@shared/queuePanelProfiles";

type MediaItem = {
  id: number;
  type: "image" | "video";
  url: string;
  title: string | null;
  displayDuration: number | null;
  externalUrl: string | null;
  externalType: "youtube" | "instagram" | "admooh" | null;
  embedHtml?: string | null;
};

function getYoutubeVideoId(url: string): string | null {
  try {
    const u = new URL(url);
    if (u.hostname.includes("youtu.be")) return u.pathname.slice(1).split("?")[0];
    if (u.hostname.includes("youtube.com")) {
      return u.searchParams.get("v") ?? u.pathname.split("/").pop() ?? null;
    }
    return null;
  } catch { return null; }
}

function normalizeInstagramUrl(rawUrl?: string | null): string | null {
  if (!rawUrl) return null;
  try {
    const url = new URL(rawUrl);
    const host = url.hostname.toLowerCase().replace(/^www\./, "").replace(/^m\./, "");
    if (host !== "instagram.com") return null;
    const parts = url.pathname.split("/").filter(Boolean);
    const contentType = parts[0] === "reels" ? "reel" : parts[0];
    const shortcode = parts[1];
    if (!shortcode || !["reel", "p", "tv"].includes(contentType)) return null;
    return `https://www.instagram.com/${contentType}/${shortcode}/`;
  } catch {
    return null;
  }
}

// O embed oficial não exige API paga, evita a dependência frágil de oEmbed/script e
// funciona para posts e Reels públicos autorizados pelo próprio Instagram.
function InstagramEmbed({ embedHtml, externalUrl, itemId }: { embedHtml?: string | null; externalUrl?: string | null; itemId: number }) {
  const cleanUrl = normalizeInstagramUrl(externalUrl);
  if (!cleanUrl) return null;
  return (
    <div key={`ig-embed-${itemId}`} style={{ width: "100%", height: "100%", position: "relative", background: "#000" }}>
      <iframe src={`${cleanUrl}embed/`} title="Conteúdo do Instagram" style={{ width: "100%", height: "100%", border: "none", background: "#000" }} allow="autoplay; encrypted-media; fullscreen" referrerPolicy="strict-origin-when-cross-origin" />
      <a href={cleanUrl} target="_blank" rel="noreferrer" style={{ position: "absolute", right: 10, bottom: 10, borderRadius: 999, background: "rgba(225,48,108,.92)", color: "#fff", padding: "7px 10px", fontSize: 11, fontWeight: 700 }}>Abrir no Instagram</a>
    </div>
  );
}

function AdmoohEmbed({ externalUrl }: { externalUrl?: string | null }) {
  if (!externalUrl) return null;
  return (
    <div style={{ width: "100%", height: "100%", position: "relative", background: "#0f172a" }}>
      <iframe
        src={externalUrl}
        title="Campanha Admoon"
        style={{ width: "100%", height: "100%", border: "none" }}
        allow="autoplay; fullscreen"
        referrerPolicy="strict-origin-when-cross-origin"
      />
      <a href={externalUrl} target="_blank" rel="noreferrer" style={{ position: "absolute", right: 10, bottom: 10, borderRadius: 999, background: "rgba(15, 23, 42, .82)", color: "#fff", padding: "7px 10px", fontSize: 11 }}>Abrir Admoon</a>
    </div>
  );
}

// Componente YouTube com autoplay real via IFrame Player API
declare global {
  interface Window {
    YT: {
      Player: new (
        el: HTMLElement,
        opts: {
          videoId: string;
          playerVars?: Record<string, number | string>;
          events?: { onReady?: (e: { target: { playVideo(): void } }) => void };
        }
      ) => { destroy(): void };
      ready: (cb: () => void) => void;
    };
    onYouTubeIframeAPIReady?: () => void;
  }
}

function loadYTApi(): Promise<void> {
  if (typeof window.YT !== "undefined" && typeof window.YT.Player !== "undefined") return Promise.resolve();
  return new Promise((resolve) => {
    if (!document.getElementById("yt-iframe-api")) {
      const tag = document.createElement("script");
      tag.id = "yt-iframe-api";
      tag.src = "https://www.youtube.com/iframe_api";
      document.head.appendChild(tag);
    }
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      prev?.();
      resolve();
    };
  });
}

function YouTubeAutoplay({ videoId, active }: { videoId: string; active: boolean }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<{ destroy(): void } | null>(null);

  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    loadYTApi().then(() => {
      if (cancelled || !containerRef.current) return;
      // Destruir player anterior se existir
      try { playerRef.current?.destroy(); } catch { /* noop */ }
      const div = document.createElement("div");
      containerRef.current.innerHTML = "";
      containerRef.current.appendChild(div);
      playerRef.current = new window.YT.Player(div, {
        videoId,
        playerVars: {
          autoplay: 1,
          mute: 1,
          loop: 1,
          playlist: videoId,
          controls: 0,
          modestbranding: 1,
          rel: 0,
          playsinline: 1,
        },
        events: {
          onReady: (e) => {
            e.target.playVideo();
          },
        },
      });
    });
    return () => {
      cancelled = true;
      try { playerRef.current?.destroy(); } catch { /* noop */ }
      playerRef.current = null;
    };
  }, [videoId, active]);

  return (
    <div
      ref={containerRef}
      style={{ width: "100%", height: "100%", background: "#000" }}
    />
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Slideshow 9:16 com loop infinito
// ─────────────────────────────────────────────────────────────────────────────
function MediaSlideshow({ salonId, salonColor }: { salonId: number; salonColor: string }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [nextIndex, setNextIndex] = useState<number | null>(null);
  const [transitioning, setTransitioning] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Refs estáveis — evitam recriação de callbacks e loops de useEffect
  const transitioningRef = useRef(false);
  const currentIndexRef = useRef(0);
  const mediaListRef = useRef<MediaItem[]>([]);

  const { data: rawMediaList = [] } = trpc.media.listActive.useQuery(
    { salonId },
    { refetchInterval: 30000 }
  );
  const mediaList = rawMediaList as MediaItem[];

  // Sincronizar refs com estado
  useEffect(() => { mediaListRef.current = mediaList; }, [mediaList]);
  useEffect(() => { currentIndexRef.current = currentIndex; }, [currentIndex]);

  const current = mediaList[currentIndex] as MediaItem | undefined;
  const next = nextIndex !== null ? (mediaList[nextIndex] as MediaItem | undefined) : undefined;

  // goNext é estável (sem dependências) — usa refs para ler estado atual
  const goNext = useCallback(() => {
    if (transitioningRef.current) return;
    const list = mediaListRef.current;
    if (list.length === 0) return;
    const idx = (currentIndexRef.current + 1) % list.length;
    transitioningRef.current = true;
    setNextIndex(idx);
    setTransitioning(true);
    setTimeout(() => {
      setCurrentIndex(idx);
      currentIndexRef.current = idx;
      setNextIndex(null);
      setTransitioning(false);
      transitioningRef.current = false;
    }, 600);
  }, []); // sem dependências — nunca recriado

  // Timer: dispara quando currentIndex muda (slide novo aparece)
  // Não depende de `transitioning` — usa ref para verificar antes de disparar
  useEffect(() => {
    const list = mediaListRef.current;
    const item = list[currentIndex] as MediaItem | undefined;
    if (!item || list.length <= 1) return;
    // Vídeo nativo sem link externo avança via onEnded
    if (item.type === "video" && !item.externalUrl) return;

    // Aguarda a transição terminar (600ms) antes de iniciar a contagem
    const dur = (item.displayDuration ?? 8) * 1000;
    const waitForTransition = setTimeout(() => {
      timerRef.current = setTimeout(goNext, dur);
    }, 650);

    return () => {
      clearTimeout(waitForTransition);
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [currentIndex, mediaList.length, goNext]);

  // Quando a lista muda de tamanho, corrigir índice fora de range
  useEffect(() => {
    if (mediaList.length > 0 && currentIndex >= mediaList.length) {
      setCurrentIndex(0);
    }
  }, [mediaList.length, currentIndex]);

  // Autoplay do vídeo quando o índice muda
  useEffect(() => {
    if (current?.type === "video" && videoRef.current) {
      videoRef.current.currentTime = 0;
      videoRef.current.play().catch(() => {});
    }
  }, [currentIndex, current?.type]);

  if (mediaList.length === 0) {
    return (
      <div
        className="w-full flex flex-col items-center justify-center gap-3 rounded-2xl border border-white/5"
        style={{
          aspectRatio: "9/16",
          background: `${salonColor}08`,
        }}
      >
        <ImageIcon className="w-12 h-12 text-white/10" />
        <p className="text-white/20 text-sm text-center px-4">
          Nenhuma mídia ativa.
          <br />
          <span className="text-xs text-white/10">Adicione em Mídia Promocional.</span>
        </p>
      </div>
    );
  }

  // Animação de slide horizontal
  const outStyle: React.CSSProperties = {
    position: "absolute",
    inset: 0,
    transform: transitioning ? "translateX(-100%)" : "translateX(0)",
    opacity: transitioning ? 0 : 1,
    transition: "transform 0.6s cubic-bezier(0.4,0,0.2,1), opacity 0.6s ease",
    zIndex: 1,
  };

  const inStyle: React.CSSProperties = {
    position: "absolute",
    inset: 0,
    transform: transitioning ? "translateX(0)" : "translateX(100%)",
    opacity: transitioning ? 1 : 0,
    transition: transitioning
      ? "transform 0.6s cubic-bezier(0.4,0,0.2,1), opacity 0.6s ease"
      : "none",
    zIndex: 2,
  };

  const mediaStyle: React.CSSProperties = {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
  };

  return (
    <div
      className="relative w-full rounded-2xl overflow-hidden border border-white/10"
      style={{ aspectRatio: "9/16" }}
    >
      {/* Slide atual (sai) */}
      <div style={outStyle}>
        {current?.externalType === "youtube" ? (
          <YouTubeAutoplay
            key={`curr-yt-${current.id}`}
            videoId={getYoutubeVideoId(current.externalUrl ?? "") ?? ""}
            active={!transitioning}
          />
        ) : current?.externalType === "instagram" ? (
          <InstagramEmbed
            key={`curr-ig-${current.id}`}
            embedHtml={current.embedHtml}
            externalUrl={current.externalUrl}
            itemId={current.id}
          />
        ) : current?.externalType === "admooh" ? (
          <AdmoohEmbed externalUrl={current.externalUrl} />
        ) : current?.type === "image" ? (
          <img
            key={`curr-${current.id}`}
            src={current.url}
            alt={current.title ?? "Mídia"}
            style={mediaStyle}
          />
        ) : current?.type === "video" ? (
          <video
            key={`curr-${current.id}`}
            ref={videoRef}
            src={current.url}
            autoPlay
            muted
            playsInline
            loop={mediaList.length === 1}
            style={mediaStyle}
            onEnded={mediaList.length > 1 ? goNext : undefined}
          />
        ) : null}
      </div>

      {/* Slide próximo (entra) */}
      {transitioning && next && (
        <div style={inStyle}>
          {next.externalType === "youtube" ? (
            <YouTubeAutoplay
              key={`next-yt-${next.id}`}
              videoId={getYoutubeVideoId(next.externalUrl ?? "") ?? ""}
              active={transitioning}
            />
          ) : next.externalType === "instagram" ? (
            <InstagramEmbed
              key={`next-ig-${next.id}`}
              embedHtml={next.embedHtml}
              externalUrl={next.externalUrl}
              itemId={next.id}
            />
          ) : next.externalType === "admooh" ? (
            <AdmoohEmbed externalUrl={next.externalUrl} />
          ) : next.type === "image" ? (
            <img
              key={`next-${next.id}`}
              src={next.url}
              alt={next.title ?? "Mídia"}
              style={mediaStyle}
            />
          ) : next.type === "video" ? (
            <video
              key={`next-${next.id}`}
              src={next.url}
              muted
              playsInline
              style={mediaStyle}
            />
          ) : null}
        </div>
      )}

      {/* Título da mídia */}
      {current?.title && (
        <div
          className="absolute bottom-0 left-0 right-0 px-4 py-3"
          style={{
            background: "linear-gradient(to top, rgba(0,0,0,0.75) 0%, transparent 100%)",
            zIndex: 3,
          }}
        >
          <p className="text-white text-sm font-medium truncate">{current.title}</p>
        </div>
      )}

      {/* Indicadores de posição */}
      {mediaList.length > 1 && (
        <div
          className="absolute bottom-8 left-0 right-0 flex justify-center gap-1.5"
          style={{ zIndex: 4 }}
        >
          {mediaList.map((_, i) => (
            <span
              key={i}
              className="rounded-full transition-all duration-300"
              style={{
                width: i === currentIndex ? "20px" : "6px",
                height: "6px",
                background:
                  i === currentIndex ? salonColor : "rgba(255,255,255,0.3)",
              }}
            />
          ))}
        </div>
      )}

      {/* Badge tipo de mídia */}
      <div
        className="absolute top-3 left-3 flex items-center gap-1 px-2 py-0.5 rounded-full bg-black/50 backdrop-blur text-white/60 text-xs"
        style={{ zIndex: 4 }}
      >
        {current?.type === "video" ? (
          <>
            <Video className="w-3 h-3" /> Vídeo
          </>
        ) : (
          <>
            <ImageIcon className="w-3 h-3" /> Imagem
          </>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Painel principal
// ─────────────────────────────────────────────────────────────────────────────
export default function AdminQueuePanel() {
  const { user } = useAuth();
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [lastCalledId, setLastCalledId] = useState<number | null>(null);
  const [salonId, setSalonId] = useState<number | null>(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  // Detectar orientação da tela
  const [isPortrait, setIsPortrait] = useState(
    () => window.matchMedia("(orientation: portrait)").matches
  );
  const prevCalledRef = useRef<Set<number>>(new Set());

  // Relógio
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Detectar mudança de orientação
  useEffect(() => {
    const mq = window.matchMedia("(orientation: portrait)");
    const handler = (e: MediaQueryListEvent) => setIsPortrait(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  const { data: salon } = trpc.salon.getMySalon.useQuery(undefined, {
    enabled: !!user,
    retry: false,
  });
  const isStorePanel = (salon as any)?.queuePanelProfile === "store";

  useEffect(() => {
    if (salon?.id) setSalonId(salon.id);
  }, [salon]);

  const { data: queue, isLoading } = trpc.queue.listPublic.useQuery(
    { salonId: salonId ?? 0 },
    { enabled: !!salonId && !isStorePanel, refetchInterval: 3000 }
  );

  // Som ao chamar
  useEffect(() => {
    if (isStorePanel || !queue) return;
    const calledItems = queue.filter((q: any) => q.status === "called");
    calledItems.forEach((item: any) => {
      if (!prevCalledRef.current.has(item.id)) {
        setLastCalledId(item.id);
        if (soundEnabled) playCallSound();
      }
    });
    prevCalledRef.current = new Set(calledItems.map((q: any) => q.id));
  }, [queue, soundEnabled, isStorePanel]);

  function playCallSound() {
    try {
      const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
      const notes = [880, 1100, 880];
      notes.forEach((freq, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.type = "sine";
        osc.frequency.setValueAtTime(freq, ctx.currentTime + i * 0.2);
        gain.gain.setValueAtTime(0.35, ctx.currentTime + i * 0.2);
        gain.gain.exponentialRampToValueAtTime(
          0.001,
          ctx.currentTime + i * 0.2 + 0.18
        );
        osc.start(ctx.currentTime + i * 0.2);
        osc.stop(ctx.currentTime + i * 0.2 + 0.2);
      });
    } catch {
      // silencioso
    }
  }

  const handleFullscreen = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      document.documentElement.requestFullscreen();
    }
  };

  const { waiting, preparing, called, inService } = summarizeMobileQueueStatuses(queue ?? []);

  const salonColor = "#C9A96E";
  const logoUrl = salon?.logoUrl;
  const salonName = salon?.name ?? "Salão";
  const panelProfile = getQueuePanelProfile((salon as any)?.queuePanelProfile);

  if (isStorePanel) {
    return (
      <ProductSalesPanel
        salonSlug={(salon as any)?.slug ?? ""}
        salonName={salonName}
        salonColor={salonColor}
        logoUrl={logoUrl}
      />
    );
  }

  // ── Coluna de informações da fila ──────────────────────────────────────────
  const QueueColumn = () => (
    <div className="flex flex-col gap-5 overflow-y-auto">

      {preparing.length > 0 && (
        <div className="rounded-2xl border border-violet-400/20 p-4 text-center" style={{ background: "rgba(139,92,246,0.08)" }}>
          <p className="text-sm font-semibold text-violet-200">{panelProfile.preparationTitle}</p>
          <p className="mt-1 text-xs text-white/55">{panelProfile.preparationDescription}</p>
          <p className="mt-2 text-xs text-violet-100/70">{panelProfile.preparationTip}</p>
        </div>
      )}

      {/* Sendo Chamado */}
      <div>
        <div className="flex items-center gap-3 mb-3">
          <span
            className="w-2.5 h-2.5 rounded-full animate-pulse"
            style={{ background: salonColor }}
          />
          <h2 className="text-xs font-semibold uppercase tracking-[0.2em] text-white/50">
            Sendo Chamado Agora
          </h2>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center h-40 rounded-2xl border border-white/5">
            <Loader2 className="w-8 h-8 animate-spin text-white/20" />
          </div>
        ) : called.length === 0 ? (
          <div className="h-40 rounded-2xl border-2 border-dashed border-white/5 flex flex-col items-center justify-center gap-3">
            <div className="w-12 h-12 rounded-full border border-white/10 flex items-center justify-center">
              <Users className="w-6 h-6 text-white/20" />
            </div>
            <p className="text-white/30 text-sm">Nenhum cliente sendo chamado</p>
          </div>
        ) : (
          <div className="space-y-3">
            {called.map((item: any) => (
              <div
                key={item.id}
                className={`rounded-2xl p-5 border-2 transition-all duration-500 ${
                  lastCalledId === item.id ? "animate-pulse" : ""
                }`}
                style={{
                  borderColor:
                    lastCalledId === item.id ? salonColor : `${salonColor}50`,
                  background:
                    lastCalledId === item.id
                      ? `linear-gradient(135deg, ${salonColor}18, ${salonColor}08)`
                      : `${salonColor}08`,
                  boxShadow:
                    lastCalledId === item.id
                      ? `0 0 40px ${salonColor}25`
                      : "none",
                }}
              >
                <div className="flex items-center justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    {(item as any).ticketNumber && (
                      <div className="inline-flex items-center gap-2 mb-2">
                        <span
                          className="text-xl font-black px-3 py-1 rounded-lg border-2 tracking-widest"
                          style={{
                            color: salonColor,
                            borderColor: salonColor,
                            background: `${salonColor}20`,
                          }}
                        >
                          {(item as any).ticketNumber}
                        </span>
                      </div>
                    )}
                    <p className="text-4xl font-bold text-white tracking-wide truncate">
                      {item.clientName}
                    </p>
                    <p
                      className="text-lg mt-1 font-medium"
                      style={{ color: salonColor }}
                    >
                      {item.serviceName}
                    </p>
                    <p className="text-sm text-white/40 mt-0.5">
                      Profissional:{" "}
                      <span className="text-white/70 font-medium">
                        {item.professionalName}
                      </span>
                    </p>
                  </div>
                  <div className="text-center shrink-0">
                    <div
                      className="w-16 h-16 rounded-full flex items-center justify-center border-2"
                      style={{
                        borderColor: salonColor,
                        background: `${salonColor}15`,
                      }}
                    >
                      <Users className="w-7 h-7" style={{ color: salonColor }} />
                    </div>
                    <p
                      className="text-xs mt-1.5 font-semibold uppercase tracking-widest"
                      style={{ color: salonColor }}
                    >
                      Dirija-se
                    </p>
                    {(item as any).stationName ? (
                      <p className="text-base font-extrabold text-white">
                        {(item as any).stationName}
                      </p>
                    ) : (
                      <p className="text-sm font-bold text-white/80">
                        {item.professionalName}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Fila de Espera — Aguardando (em cima) */}
      <div>
        <div className="flex items-center gap-3 mb-3">
          <span className="w-2.5 h-2.5 rounded-full bg-amber-400" />
          <h2 className="text-xs font-semibold uppercase tracking-[0.2em] text-white/50">
            Aguardando ({waiting.length})
          </h2>
        </div>

        {waiting.length === 0 ? (
          <div className="rounded-xl border border-dashed border-white/5 p-6 text-center">
            <CheckCircle2 className="w-7 h-7 text-white/10 mx-auto mb-2" />
            <p className="text-white/20 text-sm">Fila vazia</p>
          </div>
        ) : (
          <div className="space-y-2">
            {waiting.map((item: any, idx: number) => (
              <div
                key={item.id}
                className="rounded-xl p-3 border border-white/5 flex items-center gap-3"
                style={{ background: "rgba(255,255,255,0.03)" }}
              >
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold shrink-0"
                  style={{
                    background: `${salonColor}20`,
                    border: `1.5px solid ${salonColor}60`,
                    color: salonColor,
                  }}
                >
                  {idx + 1}
                </div>
                <div className="min-w-0">
                  <p className="font-semibold text-white truncate text-sm">
                    {item.clientName}
                  </p>
                  <p className="text-xs text-white/30 truncate">
                    {item.serviceName}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Em Atendimento — 3 colunas (embaixo) */}
      {inService.length > 0 && (
        <div>
          <div className="flex items-center gap-3 mb-3">
            <span className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
            <h2 className="text-xs font-semibold uppercase tracking-[0.2em] text-white/50">
              Em Atendimento ({inService.length})
            </h2>
          </div>
          <div className="grid grid-cols-3 gap-2">
            {inService.map((item: any) => (
              <div
                key={item.id}
                className="rounded-xl p-3 border border-emerald-900/40"
                style={{ background: "rgba(16,185,129,0.06)" }}
              >
                <p className="font-semibold text-white truncate text-sm">
                  {item.clientName}
                </p>
                <p className="text-xs text-emerald-400 mt-0.5 truncate">
                  {item.serviceName}
                </p>
                <p className="text-xs text-white/30 mt-0.5 truncate">
                  com {item.professionalName}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Boas-vindas + QR Code — lado a lado */}
      <div className="grid grid-cols-2 gap-3">
        {/* Card boas-vindas */}
        <div
          className="rounded-xl p-4 border border-white/5 text-center flex flex-col items-center justify-center"
          style={{
            background: `linear-gradient(135deg, ${salonColor}10, ${salonColor}05)`,
          }}
        >
          {logoUrl ? (
            <img
              src={logoUrl}
              alt={salonName}
              className="w-10 h-10 rounded-lg object-cover mb-2 border border-white/10"
            />
          ) : (
            <div
              className="w-10 h-10 rounded-lg mb-2 flex items-center justify-center"
              style={{ background: `${salonColor}20` }}
            >
              <Scissors className="w-5 h-5" style={{ color: salonColor }} />
            </div>
          )}
          <p className="font-semibold text-white text-sm">{salonName}</p>
          <p className="text-white/40 text-xs mt-0.5">Bem-vindo(a)!</p>
          <p className="text-white/25 text-xs">Aguarde seu nome ser chamado.</p>
        </div>

        {/* Card QR Code */}
        {salonId ? (
          <div
            className="rounded-xl p-4 border border-white/5 text-center flex flex-col items-center justify-center"
            style={{ background: "rgba(255,255,255,0.02)" }}
          >
            <div className="flex items-center justify-center gap-1 mb-2">
              <QrCode className="w-3 h-3 text-white/40" />
              <p className="text-xs text-white/40 uppercase tracking-widest font-medium">
                Acompanhe
              </p>
            </div>
            <div className="inline-block p-1.5 rounded-lg bg-white">
              <QRCodeSVG
                value={`${window.location.origin}/checkin/${salonId}`}
                size={72}
                bgColor="#ffffff"
                fgColor="#0a0a0f"
                level="M"
              />
            </div>
            <p className="text-white/25 text-xs mt-1.5">
              Escaneie para fazer check-in
            </p>
          </div>
        ) : (
          <div className="rounded-xl border border-white/5" style={{ background: "rgba(255,255,255,0.02)" }} />
        )}
      </div>
    </div>
  );

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div
      className="h-screen overflow-hidden flex flex-col"
      style={{
        background:
          "linear-gradient(135deg, #0a0a0f 0%, #111118 60%, #0d0d15 100%)",
      }}
    >
      {/* Header */}
      <div
        className="border-b border-white/5 px-4 py-3 flex items-center justify-between shrink-0"
        style={{
          background: "rgba(255,255,255,0.03)",
          backdropFilter: "blur(8px)",
        }}
      >
        <div className="flex items-center gap-3">
          {logoUrl ? (
            <img
              src={logoUrl}
              alt={salonName}
              className="w-10 h-10 rounded-xl object-cover border border-white/10 shadow-lg"
              style={{ boxShadow: `0 0 16px ${salonColor}40` }}
            />
          ) : (
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center border border-white/10"
              style={{ background: `${salonColor}20` }}
            >
              <Scissors className="w-5 h-5" style={{ color: salonColor }} />
            </div>
          )}
          <div>
            <h1 className="text-lg font-bold text-white tracking-wide">
              {salonName}
            </h1>
            <p className="text-xs text-white/40 flex items-center gap-1">
              <Wifi className="w-3 h-3" />
              Painel de Chamada
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* Relógio */}
          <div className="text-right">
            <p className="text-2xl font-bold text-white tabular-nums tracking-tight">
              {format(currentTime, "HH:mm", { locale: ptBR })}
            </p>
            <p className="text-xs text-white/40 capitalize">
              {format(currentTime, "EEE, dd/MM", { locale: ptBR })}
            </p>
          </div>
          {/* Controles */}
          <div className="flex items-center gap-1">
            <Button
              size="sm"
              variant="ghost"
              className="text-white/40 hover:text-white hover:bg-white/10 rounded-lg"
              onClick={() => setSoundEnabled(!soundEnabled)}
              title={soundEnabled ? "Desativar som" : "Ativar som"}
            >
              {soundEnabled ? (
                <Volume2 className="w-4 h-4" />
              ) : (
                <VolumeX className="w-4 h-4 text-red-400" />
              )}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="text-white/40 hover:text-white hover:bg-white/10 rounded-lg"
              onClick={handleFullscreen}
              title="Tela cheia"
            >
              <Maximize2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {isPortrait && <MobileQueueStatusBar waiting={waiting.length} preparing={preparing.length} called={called.length} inService={inService.length} />}

      {/* ── Corpo principal ── */}
      {isPortrait ? (
        // ── MODO VERTICAL (portrait) ─────────────────────────────────────
        // Mídia fixada no topo (não rola), fila com scroll independente abaixo
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Mídia: altura fixa no topo, nunca rola */}
          {salonId && (
            <div
              className="shrink-0 w-full px-4 pt-4 pb-2"
              style={{ maxHeight: "45vh" }}
            >
              <MediaSlideshow salonId={salonId} salonColor={salonColor} />
            </div>
          )}
          {/* Fila: área com scroll independente */}
          <div className="flex-1 overflow-y-auto px-4 pb-4">
            <QueueColumn />
          </div>
        </div>
      ) : (
        // ── MODO HORIZONTAL (landscape / TV / desktop) ────────────────────
        // Fila na esquerda com scroll independente, mídia FIXA na direita (nunca rola)
        <div className="flex-1 flex overflow-hidden">
          {/* Coluna esquerda: fila com scroll próprio */}
          <div className="flex-1 overflow-y-auto p-5">
            <QueueColumn />
          </div>

          {/* Coluna direita: mídia 9:16 — fixa, nunca rola */}
          {salonId && (
            <div
              className="shrink-0 flex items-stretch overflow-hidden"
              style={{
                // Largura calculada para manter proporção 9:16 em relação à altura disponível
                // Altura disponível = 100vh - header (~56px) - footer (~40px)
                width: "calc((100vh - 96px) * 9 / 16)",
                maxWidth: "38vw",
                minWidth: "180px",
              }}
            >
              <div className="flex-1 p-3 flex items-center justify-center">
                <MediaSlideshow salonId={salonId} salonColor={salonColor} />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Footer */}
      <div
        className="border-t border-white/5 px-5 py-2.5 flex items-center justify-between shrink-0"
        style={{ background: "rgba(255,255,255,0.02)" }}
      >
        <p className="text-xs text-white/20 capitalize">
          {format(currentTime, "EEEE, dd 'de' MMMM 'de' yyyy", {
            locale: ptBR,
          })}
        </p>
        <div className="flex items-center gap-2">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
          <p className="text-xs text-white/20">Atualiza automaticamente</p>
        </div>
      </div>
    </div>
  );
}
